import { type InfiniteData } from "@tanstack/react-query";
import sumBy from "lodash/sumBy";

import { queryClient, QueryKeys } from "@/api/core";
import {
  type ChatMessage,
  chatMessageSchema,
  type Chat,
  chatSchema,
} from "@/schemas";
import { chatSearchHistory } from "@/settings";

import { compareDate } from "./comparators";
import {
  flattenPages,
  type PaginatedResult,
  prependPageItem,
  removePageItem,
  sortQueryData,
  updatePageItem,
  updateQueryData,
} from "./queries";

const RECENT_SEARCHES_KEY = "soapbox:recent-chat-searches";

/**
 * Update the Chat entity inside the ChatSearch query.
 * @param newChat - Chat entity.
 */
const updateChatInChatSearchQuery = (newChat: Chat) => {
  updateQueryData(QueryKeys.chats.global, newChat, chatSchema);
};

/**
 * Re-order the ChatSearch query by the last message timestamp.
 */
const reOrderChatListItems = () => {
  sortQueryData<Chat>(QueryKeys.chats.list(), (chatA, chatB) => {
    return compareDate(
      chatA.last_message?.created_at as string,
      chatB.last_message?.created_at as string,
    );
  });
};

/**
 * Check if a Chat entity exists within the cached ChatSearch query.
 * @param chatId - String
 * @returns Boolean
 */
const checkIfChatExists = (chatId: string) => {
  const currentChats = flattenPages(
    queryClient.getQueryData<InfiniteData<PaginatedResult<Chat>>>(
      QueryKeys.chats.list(),
    ),
  );

  return currentChats?.find((chat: Chat) => chat.id === chatId);
};

/**
 * Force a re-fetch of ChatSearch.
 */
const invalidateChatSearchQuery = () => {
  queryClient.invalidateQueries({ queryKey: QueryKeys.chats.list() });
};

const updateChatListItem = (newChat: Chat) => {
  const { id: chatId, last_message: lastMessage } = newChat;

  const isChatAlreadyLoaded = checkIfChatExists(chatId);

  if (isChatAlreadyLoaded) {
    // If the chat exists in the client, let's update it.
    updateChatInChatSearchQuery(newChat);
    // Now that we have the new chat loaded, let's re-sort to put
    // the most recent on top.
    reOrderChatListItems();
  } else {
    // If this is a brand-new chat, let's invalid the queries.
    invalidateChatSearchQuery();
  }

  if (lastMessage) {
    // Update the Chat Messages query data.
    prependPageItem(
      QueryKeys.chatMessages.forChat(newChat.id),
      chatMessageSchema.parse(lastMessage),
    );
  }
};

/** Get unread chats count. */
const getUnreadChatsCount = (): number => {
  const chats = flattenPages(
    queryClient.getQueryData<InfiniteData<PaginatedResult<Chat>>>(
      QueryKeys.chats.list(),
    ),
  );

  return sumBy(chats, (chat) => chat.unread);
};

/** Update the query cache for an individual Chat Message */
const updateChatMessage = (chatMessage: ChatMessage) =>
  updatePageItem(
    QueryKeys.chatMessages.forChat(chatMessage.chat_id),
    chatMessageSchema.parse(chatMessage),
    (o, n) => o.id === n.id,
  );

const clearRecentSearches = (currentUserId: string) =>
  chatSearchHistory.remove(currentUserId);

const saveChatSearch = (currentUserId: string, search: string) => {
  let currentSearches: string[] = [];

  if (chatSearchHistory.get(currentUserId)) {
    currentSearches = chatSearchHistory.get(currentUserId);
  }

  if (currentSearches.indexOf(search) === -1) {
    currentSearches.unshift(search);
    if (currentSearches.length > 10) {
      currentSearches.pop();
    }

    chatSearchHistory.set(currentUserId, currentSearches);

    return currentSearches;
  } else {
    // The search term has already been searched. Move it to the beginning
    // of the cached list.
    const indexOfSearch = currentSearches.indexOf(search);
    const nextCurrentSearches = [...currentSearches];
    nextCurrentSearches.splice(
      0,
      0,
      ...nextCurrentSearches.splice(indexOfSearch, 1),
    );
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(nextCurrentSearches),
    );

    return nextCurrentSearches;
  }
};

/** Check if item is most recent */
const isLastMessage = (chatMessageId: string): boolean => {
  const queryData = queryClient.getQueryData<
    InfiniteData<PaginatedResult<Chat>>
  >(QueryKeys.chats.list());
  const items = flattenPages<Chat>(queryData);
  const chat = items?.find((item) => item.last_message?.id === chatMessageId);

  return !!chat;
};

const removeChatMessage = (payload: string) => {
  const data = JSON.parse(payload);
  const chatId = data.chat_id;
  const chatMessageId = data.deleted_message_id;

  // If the user just deleted the "last_message", then let's invalidate
  // the Chat Search query so the Chat List will show the new "last_message".
  if (isLastMessage(chatMessageId)) {
    queryClient.invalidateQueries({ queryKey: QueryKeys.chats.list() });
  }

  removePageItem(
    QueryKeys.chatMessages.forChat(chatId),
    chatMessageId,
    (o: any, n: any) => String(o.id) === String(n),
  );
};

export {
  clearRecentSearches,
  getUnreadChatsCount,
  reOrderChatListItems,
  saveChatSearch,
  updateChatListItem,
  updateChatMessage,
  isLastMessage,
  removeChatMessage,
};
